import React, { useEffect, useMemo, useState } from "react";
import {
  Package,
  AlertTriangle,
  Boxes,
  ShoppingCart,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { notify } from "../utils/notify";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { usePanelTheme } from "../context/themeContext";
import StatCard from "../components/StatCard";
import UIButton from "../components/UIButton";
import PageShell from "../components/PageShell";
import PanelCard from "../components/PanelCard";
import SectionHeading from "../components/SectionHeading";

export default function Materials({ t = {}, lang = "de" }) {
  const { currentTheme, theme } = usePanelTheme();

  const [name, setName] = useState("");
  const [supplier, setSupplier] = useState("");
  const [quantity, setQuantity] = useState("");
  const [unit, setUnit] = useState("pcs");
  const [price, setPrice] = useState("");
  const [status, setStatus] = useState("InStock");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mobileSection, setMobileSection] = useState("form");
  const [projectId, setProjectId] = useState("");
  const [projectName, setProjectName] = useState("");

  const [editId, setEditId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const isGerman = lang === "de" || t?.german === "Deutsch";

  const materialUnitLabels = {
    pcs: t?.materialUnitPieces || (isGerman ? "Stk" : "copë"),
    kg: t?.materialUnitKg || "kg",
    m2: t?.materialUnitM2 || "m²",
    m3: t?.materialUnitM3 || "m³",
    box: t?.materialUnitBox || (isGerman ? "Kiste" : "kuti"),
  };

  const materialStatusLabels = {
    InStock: t?.materialStatusInStock || (isGerman ? "Auf Lager" : "Në stok"),
    LowStock: t?.materialStatusLowStock || (isGerman ? "Wenig Bestand" : "Stok i ulët"),
    Ordered: t?.materialStatusOrdered || (isGerman ? "Bestellt" : "Porositur"),
  };

  const historyTitle =
    t?.materialsHistoryTitle ||
    t?.materialsHistory ||
    (isGerman ? "Materialverlauf" : "Historia e materialeve");

  const recordsLabel =
    t?.records || (isGerman ? "Einträge" : "regjistrime");

  const prevLabel = t?.prev || (isGerman ? "Zurück" : "Mbrapa");
  const nextLabel = t?.next || (isGerman ? "Weiter" : "Para");
  const pageLabel = t?.page || (isGerman ? "Seite" : "Faqja");
  const ofLabel = t?.of || (isGerman ? "von" : "nga");

  const formatCurrency = (value) =>
    new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(Number(value) || 0);


const loadProjects = async () => {
  try {
    const snap = await getDocs(query(collection(db, "projects"), orderBy("createdAt", "desc")));
    setProjects(
      snap.docs.map((item) => ({
        id: item.id,
        name: item.data()?.name || "",
      }))
    );
  } catch (error) {
    console.warn("Projects load skipped:", error);
    setProjects([]);
  }
};

const loadMaterials = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, "materials"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setItems(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
    } catch (error) {
      console.error("Error loading materials:", error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMaterials();
    loadProjects();
  }, []);

  const totalValue = useMemo(
    () =>
      items.reduce(
        (sum, item) =>
          sum + (Number(item.quantity) || 0) * (Number(item.price) || 0),
        0
      ),
    [items]
  );

  const lowStockCount = useMemo(
    () => items.filter((item) => item.status === "LowStock").length,
    [items]
  );

  const inStockCount = useMemo(
    () => items.filter((item) => item.status === "InStock").length,
    [items]
  );

  const orderedCount = useMemo(
    () => items.filter((item) => item.status === "Ordered").length,
    [items]
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = items.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.max(1, Math.ceil(items.length / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const resetForm = () => {
    setName("");
    setSupplier("");
    setQuantity("");
    setUnit("pcs");
    setPrice("");
    setStatus("InStock");
    setDate(new Date().toISOString().slice(0, 10));
    setProjectId("");
    setProjectName("");
    setEditId(null);
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setName(item.name || "");
    setSupplier(item.supplier || "");
    setQuantity(item.quantity || "");
    setUnit(item.unit || "pcs");
    setPrice(item.price || "");
    setStatus(item.status || "InStock");
    setDate(item.date || new Date().toISOString().slice(0, 10));
    setProjectId(item.projectId || "");
    setProjectName(item.projectName || "");
    setMobileSection("form");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    const confirmText =
      t?.confirmDeleteMaterial ||
      (isGerman
        ? "Möchtest du dieses Material wirklich löschen?"
        : "A dëshiron ta fshish këtë material?");

    if (!window.confirm(confirmText)) return;

    try {
      await deleteDoc(doc(db, "materials", id));
      notify.success(isGerman ? "Erfolgreich gelöscht" : "U fshi me sukses");

      if (editId === id) resetForm();
      await loadMaterials();

      if (currentItems.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (error) {
      console.error("Error deleting material:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !quantity) return;

    try {
      setSaving(true);

      const entry = {
        name,
        supplier: supplier || "",
        quantity: Number(quantity) || 0,
        unit,
        price: Number(price) || 0,
        status,
        date,
        projectId: projectId || "",
        projectName: projectName || projects.find((item) => item.id === projectId)?.name || "",
      };

      if (editId) {
        await updateDoc(doc(db, "materials", editId), entry);
        notify.success(
          t?.updateSuccess || (isGerman ? "Erfolgreich aktualisiert" : "U përditësua me sukses")
        );
      } else {
        entry.createdAt = serverTimestamp();
        await addDoc(collection(db, "materials"), entry);
        notify.success(t?.savedSuccessfully || "Materiali u ruajt me sukses");
      }

      resetForm();
      await loadMaterials();
      setCurrentPage(1);
    } catch (error) {
      console.error("Error saving material:", error);
      notify.error(
        t?.saveErrorMaterial ||
          (isGerman ? "Fehler beim Speichern." : "Gabim gjatë ruajtjes.")
      );
    } finally {
      setSaving(false);
    }
  };

  const inputClass = `h-12 w-full rounded-2xl border px-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 ${
    theme === "dark"
      ? "border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-500"
      : "border-slate-200 bg-slate-50 text-slate-900 focus:bg-white"
  }`;

  const labelClass = `mb-2 block text-sm font-semibold ${
    theme === "dark" ? "text-slate-300" : "text-slate-700"
  }`;

  const getStatusBadge = (value) => {
    const base = "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold";
    if (value === "InStock") {
      return theme === "dark"
        ? `${base} bg-emerald-950 text-emerald-300`
        : `${base} bg-emerald-100 text-emerald-700`;
    }
    if (value === "LowStock") {
      return theme === "dark"
        ? `${base} bg-amber-950 text-amber-300`
        : `${base} bg-amber-100 text-amber-700`;
    }
    return theme === "dark"
      ? `${base} bg-blue-950 text-blue-300`
      : `${base} bg-blue-100 text-blue-700`;
  };

  return (
    <PageShell>
      <SectionHeading
        title={t?.materialsTitle || "Materiale"}
        subtitle={
          t?.materialsSubtitle ||
          (isGerman
            ? "Verwalte Materialien, Bestand, Lieferanten und Lagerwert an einem Ort."
            : "Menaxho materialet, stokun, furnitorët dhe vlerën e magazinës në një vend.")
        }
      />

      <div className="grid min-w-0 grid-cols-2 gap-2.5 xl:grid-cols-4">
        <StatCard
          title={t?.totalMaterialsValue || (isGerman ? "Gesamtwert Lager" : "Vlera totale e stokut")}
          value={formatCurrency(totalValue)}
          subtitle={
            t?.totalMaterialsValueSubtitle ||
            (isGerman
              ? "Geschätzter Gesamtwert aller Materialien"
              : "Vlera e përgjithshme e materialeve")
          }
          icon={Package}
          accent="blue"
        />
        <StatCard
          title={t?.lowStockCount || (isGerman ? "Wenig Bestand" : "Stok i ulët")}
          value={String(lowStockCount)}
          subtitle={
            t?.lowStockCountSubtitle ||
            (isGerman ? "Materialien mit niedrigem Bestand" : "Materiale me stok të ulët")
          }
          icon={AlertTriangle}
          accent="amber"
        />
        <StatCard
          title={t?.inStockCount || (isGerman ? "Auf Lager" : "Në stok")}
          value={String(inStockCount)}
          subtitle={
            t?.inStockCountSubtitle ||
            (isGerman ? "Verfügbare Materialien" : "Materiale të disponueshme")
          }
          icon={Boxes}
          accent="emerald"
        />
        <StatCard
          title={t?.orderedCount || (isGerman ? "Bestellt" : "Porositur")}
          value={String(orderedCount)}
          subtitle={
            t?.orderedCountSubtitle ||
            (isGerman ? "Bestellte Materialien" : "Materiale të porositura")
          }
          icon={ShoppingCart}
          accent="violet"
        />
      </div>

      <div className="xl:hidden">
        <PanelCard className="p-2">
          <div className="grid grid-cols-2 gap-2">
            <UIButton
              type="button"
              onClick={() => setMobileSection("form")}
              theme={theme}
              variant={mobileSection === "form" ? "primary" : "secondary"}
              size="lg"
              className="w-full"
            >
              {editId ? (t?.update || "Përditëso") : (t?.addMaterialTitle || "Shto material")}
            </UIButton>

            <UIButton
              type="button"
              onClick={() => setMobileSection("history")}
              theme={theme}
              variant={mobileSection === "history" ? "primary" : "secondary"}
              size="lg"
              className="w-full"
            >
              {historyTitle}
            </UIButton>
          </div>
        </PanelCard>
      </div>

      <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[440px_minmax(0,1fr)]">
        <div className={mobileSection === "history" ? "hidden xl:block" : "block"}>
          <PanelCard>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h3
                  className={`text-xl font-bold tracking-tight ${
                    theme === "dark" ? "text-slate-100" : "text-slate-900"
                  }`}
                >
                  {editId ? (t?.update || "Përditëso") : (t?.addMaterialTitle || "Shto material")}
                </h3>
                <p className={`mt-1 text-sm ${currentTheme.muted}`}>
                  {t?.addMaterialSubtitle ||
                    (isGerman
                      ? "Erfasse neue Materialien mit Bestand, Preis und Lieferant."
                      : "Regjistro materiale të reja me sasi, çmim dhe furnitor.")}
                </p>
              </div>

              {editId && (
                <UIButton
                  type="button"
                  onClick={resetForm}
                  theme={theme}
                  variant="softDanger"
                  size="sm"
                >
                  {t?.cancel || "Anulo"}
                </UIButton>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={labelClass}>
                  {t?.materialName || (isGerman ? "Materialname" : "Emri i materialit")}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t?.materialNamePlaceholder || (isGerman ? "Z. B. Zement" : "P.sh. Çimento")}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>
                  {t?.supplier || (isGerman ? "Lieferant" : "Furnitori")}
                </label>
                <input
                  type="text"
                  value={supplier}
                  onChange={(e) => setSupplier(e.target.value)}
                  placeholder={
                    t?.supplierPlaceholder ||
                    (isGerman ? "Z. B. Bau Partner GmbH" : "P.sh. Partneri i ndërtimit")
                  }
                  className={inputClass}
                />
              </div>


<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
  <div>
    <label className={labelClass}>
      {isGerman ? "Projekt" : "Projekti"}
    </label>
    <select
      value={projectId}
      onChange={(e) => {
        const nextId = e.target.value;
        const selectedProject = projects.find((item) => item.id === nextId);
        setProjectId(nextId);
        setProjectName(selectedProject?.name || "");
      }}
      className={inputClass}
    >
      <option value="">{isGerman ? "Kein verknüpftes Projekt" : "Pa projekt të lidhur"}</option>
      {projects.map((project) => (
        <option key={project.id} value={project.id}>
          {project.name || (isGerman ? "Ohne Namen" : "Pa emër")}
        </option>
      ))}
    </select>
  </div>

  <div>
    <label className={labelClass}>
      {isGerman ? "Projektname" : "Emri i projektit"}
    </label>
    <input
      type="text"
      value={projectName}
      onChange={(e) => setProjectName(e.target.value)}
      placeholder={isGerman ? "Fallback Projektname" : "Emër projekti si fallback"}
      className={inputClass}
    />
  </div>
</div>

<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
  <div>
    <label className={labelClass}>
      {t?.quantity || (isGerman ? "Menge" : "Sasia")}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder="0"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    {t?.unit || (isGerman ? "Einheit" : "Njësia")}
                  </label>
                  <select
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    className={inputClass}
                  >
                    <option value="pcs">{materialUnitLabels.pcs}</option>
                    <option value="kg">{materialUnitLabels.kg}</option>
                    <option value="m2">{materialUnitLabels.m2}</option>
                    <option value="m3">{materialUnitLabels.m3}</option>
                    <option value="box">{materialUnitLabels.box}</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className={labelClass}>
                    {t?.pricePerUnit || (isGerman ? "Preis pro Einheit" : "Çmimi për njësi")}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="0.00"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>{t?.date || "Data"}</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>{t?.status || "Statusi"}</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className={inputClass}
                >
                  <option value="InStock">{materialStatusLabels.InStock}</option>
                  <option value="LowStock">{materialStatusLabels.LowStock}</option>
                  <option value="Ordered">{materialStatusLabels.Ordered}</option>
                </select>
              </div>

              <UIButton
                type="submit"
                disabled={saving}
                theme={theme}
                variant="primary"
                size="xl"
                className="mt-2 w-full"
              >
                {saving
                  ? (t?.savingMaterial || "Duke ruajtur...")
                  : (editId ? (t?.update || "Përditëso") : (t?.saveMaterial || "Ruaj materialin"))}
              </UIButton>
            </form>
          </PanelCard>
        </div>

        <PanelCard>
          <div className="mb-6 flex items-center justify-between gap-3">
            <div>
              <h3
                className={`text-xl font-bold tracking-tight ${
                  theme === "dark" ? "text-slate-100" : "text-slate-900"
                }`}
              >
                {historyTitle}
              </h3>
              <p className={`mt-1 text-sm ${currentTheme.muted}`}>
                {t?.materialsHistorySubtitle ||
                  (isGerman
                    ? "Sieh dir Materialbestand, Lieferanten und Werte an."
                    : "Shiko stokun, furnitorët dhe vlerën e materialeve.")}
              </p>
            </div>
            <div className={`rounded-2xl px-3 py-2 text-sm font-semibold ${currentTheme.surfaceSoft} ${currentTheme.muted}`}>
              {items.length} {recordsLabel}
            </div>
          </div>

          <div className="flex-1">
            {loading ? (
              <div
                className={`flex min-h-[320px] items-center justify-center rounded-3xl border border-dashed text-center ${
                  theme === "dark"
                    ? "border-slate-700 bg-slate-900/60"
                    : "border-slate-200 bg-slate-50"
                }`}
              >
                <p className={`text-base font-semibold ${theme === "dark" ? "text-slate-200" : "text-slate-700"}`}>
                  {t?.loading || "Duke ngarkuar..."}
                </p>
              </div>
            ) : currentItems.length === 0 ? (
              <div
                className={`flex min-h-[320px] items-center justify-center rounded-3xl border border-dashed text-center ${
                  theme === "dark"
                    ? "border-slate-700 bg-slate-900/60"
                    : "border-slate-200 bg-slate-50"
                }`}
              >
                <div>
                  <p className={`text-base font-semibold ${theme === "dark" ? "text-slate-200" : "text-slate-700"}`}>
                    {t?.noMaterialsYet || (isGerman ? "Noch keine Materialien vorhanden" : "Ende nuk ka materiale")}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {currentItems.map((item, index) => (
                  <div
                    key={item.id || index}
                    className={`flex flex-col gap-4 rounded-3xl border p-4 transition shadow-[0_12px_30px_rgba(15,23,42,0.04)] md:flex-row md:items-start md:justify-between ${
                      theme === "dark"
                        ? "border-slate-700 bg-slate-900/60 hover:bg-slate-900 hover:border-slate-600"
                        : "border-slate-200 bg-slate-50/80 hover:bg-white hover:border-slate-300"
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className={`text-base font-semibold ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>
                          {item.name || "-"}
                        </h4>
                        <span className={getStatusBadge(item.status)}>
                          {materialStatusLabels[item.status] || item.status || materialStatusLabels.InStock}
                        </span>
                      </div>

                      <p className={`mt-1 text-sm font-medium ${theme === "dark" ? "text-slate-300" : "text-slate-700"}`}>
                        {(Number(item.quantity) || 0).toLocaleString("de-DE")}{" "}
                        {materialUnitLabels[item.unit] || item.unit || materialUnitLabels.pcs}
                      </p>

                      <p className={`mt-1 truncate text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                        {item.supplier || t?.noDescription || (isGerman ? "Kein Lieferant" : "Pa furnitor")}
                      </p>

                      <div className={`mt-3 text-sm ${currentTheme.muted}`}>
                        {item.date || "-"}
                      </div>
                    </div>

                    <div className="flex flex-col items-start gap-2 md:items-end">
                      <div className={`text-sm font-semibold ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>
                        {formatCurrency((Number(item.quantity) || 0) * (Number(item.price) || 0))}
                      </div>

                      <div className={`text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
                        {formatCurrency(item.price)} /{" "}
                        {materialUnitLabels[item.unit] || item.unit || materialUnitLabels.pcs}
                      </div>

                      <div className="mt-2 flex items-center gap-2">
                        <UIButton
                          type="button"
                          onClick={() => handleEdit(item)}
                          theme={theme}
                          variant="primary"
                          size="sm"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">{t?.edit || "Ndrysho"}</span>
                        </UIButton>

                        <UIButton
                          type="button"
                          onClick={() => handleDelete(item.id)}
                          theme={theme}
                          variant="softDanger"
                          size="sm"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">{t?.delete || "Fshij"}</span>
                        </UIButton>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between border-t border-slate-200/60 pt-5 dark:border-slate-700/60">
              <UIButton
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                theme={theme}
                variant="secondary"
                size="md"
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="hidden sm:inline">{prevLabel}</span>
              </UIButton>

              <span className={`text-sm font-semibold ${currentTheme.muted}`}>
                {pageLabel} {currentPage} {ofLabel} {totalPages}
              </span>

              <UIButton
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                theme={theme}
                variant="secondary"
                size="md"
              >
                <span className="hidden sm:inline">{nextLabel}</span>
                <ChevronRight className="h-4 w-4" />
              </UIButton>
            </div>
          )}
        </PanelCard>
      </div>
    </PageShell>
  );
}
